import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import { useAuthSession } from 'modules/auth-session';

import {
  useConfirmConnectAccountMutation,
  useConnectAccountMutation,
  useDisconnectAccountMutation,
  useGetGoAccountsMutation,
  useSetGoAccountsMutation,
} from '../api/grex.api';
import { GrexAC, setGoAccounts, updateAccount } from '../store/grex.slice';
import {
  ConfirmConnectAccountPayload,
  ConnectAccountPayload,
  DisconnectAccountPayload,
  EGrexCountry,
  GetGoAccountsPayload,
  SaveGoAccountsPayload,
} from '../types/grex.types';

interface UseGrex {
  connectAccountHandler: (payload: ConnectAccountPayload) => Promise<string | null>;
  disconnectAccountHandler: (country: EGrexCountry, payload: DisconnectAccountPayload) => Promise<boolean>;
  confirmConnectAccountHandler: (country: EGrexCountry, payload: ConfirmConnectAccountPayload) => Promise<boolean>;
  getGoAccountsHandler: (country: EGrexCountry, payload: GetGoAccountsPayload) => Promise<void>;
  setGoAccountsHandler: (country: EGrexCountry, payload: SaveGoAccountsPayload) => Promise<boolean>;
}

export const useGrex = (): UseGrex => {
  const dispatch = useAppDispatch();
  const { checkSessionHandler } = useAuthSession();

  const [connectAccountQuery] = useConnectAccountMutation();
  const [confirmConnectAccountQuery] = useConfirmConnectAccountMutation();
  const [disconnectAccountQuery] = useDisconnectAccountMutation();
  const [getGoAccountsQuery] = useGetGoAccountsMutation();
  const [setGoAccountsQuery] = useSetGoAccountsMutation();

  const connectAccountHandler = async (payload: ConnectAccountPayload) => {
    dispatch(GrexAC.toggleLoading(true));
    const { data: response, error } = await connectAccountQuery(payload);
    dispatch(GrexAC.toggleLoading(false));
    const isError = error || !response;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to connect account!');
      return null;
    }
    return response.data.url;
  };

  const disconnectAccountHandler = async (country: EGrexCountry, payload: DisconnectAccountPayload) => {
    dispatch(GrexAC.toggleLoading(true));
    const { data: response, error } = await disconnectAccountQuery(payload);
    dispatch(GrexAC.toggleLoading(false));
    const isError = error || !response;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to disconnect account!');
      return false;
    }
    dispatch(updateAccount({ country, account: null }));
    return true;
  };

  const confirmConnectAccountHandler = async (country: EGrexCountry, payload: ConfirmConnectAccountPayload) => {
    dispatch(GrexAC.toggleLoading(true));
    const { data: response, error } = await confirmConnectAccountQuery(payload);
    dispatch(GrexAC.toggleLoading(false));
    const isError = error || !response;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to connect account!');
      return false;
    }
    dispatch(updateAccount({ country, account: response.data }));
    return true;
  };

  const getGoAccountsHandler = async (country: EGrexCountry, payload: GetGoAccountsPayload) => {
    dispatch(GrexAC.toggleLoading(true));
    const { data: response, error } = await getGoAccountsQuery(payload);
    dispatch(GrexAC.toggleLoading(false));
    const isError = error || !response?.data;

    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get GO accounts!');
      return;
    }
    dispatch(setGoAccounts({ country, goAccounts: response.data }));
  };

  const setGoAccountsHandler = async (country: EGrexCountry, payload: SaveGoAccountsPayload) => {
    dispatch(GrexAC.toggleLoading(true));
    const { error } = await setGoAccountsQuery(payload);

    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to set GO accounts!');
      dispatch(GrexAC.toggleLoading(false));
      return false;
    }

    await checkSessionHandler();
    dispatch(GrexAC.toggleLoading(false));
    return true;
  };

  return {
    connectAccountHandler,
    disconnectAccountHandler,
    confirmConnectAccountHandler,
    getGoAccountsHandler,
    setGoAccountsHandler,
  };
};
